import { Row, Col, message, Table, Pagination, Button, Popconfirm, Switch, Tag, Tooltip } from "antd";
import styled from "styled-components";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useState } from "react";
import { MdEdit, MdHistory } from "react-icons/md";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppFilterButton, AppFilter } from "components/AppFilter";
import { getCities, getClusterListing, getSocieties, submitSocietyAmendment } from "../../store/admin-slice/adminServicesSlice";
import { BsBuilding } from "react-icons/bs";
import { variables } from "common/variables";
import { Society } from "common/interface/Society";
import { AppSocietyFilter } from "components/AppFilter/AppSocietyFilter";
import API, { apis } from "common/apis";
import { SocietyHistoryModal } from "./component/SocietyHistoryModal";

type PartialAdminUserType = Partial<Society> & { key: string; pendingAmendmentId?: number };
const MESSAGE_KEY = "all_admin_users";

const headerKeys: { [key: string]: string } = {
    id: " ID",
    name: "Name",
    // coordinates: "Coordinates",
    // tower: "Tower",
    storeId: "Store ID",
    clusterId: "Cluster ID",
    pincode: "Pincode",
    cityId: "City",
    // state: "State",
    verified: "Verified Status",
    approved: "Approved Status",
    actions: "Actions",
    status: "Status"
};

const StyledActionButton = styled.button`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  cursor: pointer;
`;

const StyledActionLink = styled(NavLink)`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledButton = styled(Button)`
  &.filterButton {
    &--active {
      border-color: ${variables.primaryColor} !important;
    }
  }
`;

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

interface SocietyStatusToggleProps {
    record: PartialAdminUserType;
    globalStatus: string;
    onToggle: (id: any, newStatus: number) => void;
    disabled?: boolean;
}

const SocietyStatusToggle: React.FC<SocietyStatusToggleProps> = ({ record, globalStatus, onToggle, disabled }) => {
    const isSocietyActive = (record as Society).active === 1;
    const [customerCount, setCustomerCount] = useState<number | null>(null);
    const [countLoading, setCountLoading] = useState(false);

    const handleVisibleChange = (visible: boolean) => {
        if (visible && isSocietyActive) {
            setCountLoading(true);
            setCustomerCount(null);
            API.get(`${apis.addNewSociety}/${record.id}/customer-count`)
                .then(res => setCustomerCount(res.data?.count ?? 0))
                .catch(() => setCustomerCount(0))
                .finally(() => setCountLoading(false));
        }
        if (!visible) {
            setCustomerCount(null);
            setCountLoading(false);
        }
    };

    return (
        <Popconfirm
            title={
                <>
                    {isSocietyActive && (
                        <>
                            <p style={{ color: 'red', marginBottom: '4px' }}>
                                {countLoading
                                    ? 'Loading affected address count...'
                                    : `${customerCount ?? 0} active addresses will be affected by this action.`}
                            </p>
                            <p style={{ color: '#ff7875', marginBottom: '8px', fontSize: '12px' }}>
                                All orders & repeat orders will get cancelled once deactivated
                            </p>
                        </>
                    )}
                    <p>This will submit a status change request for approval. Continue?</p>
                </>
            }
            disabled={globalStatus === "updating" || disabled}
            onVisibleChange={handleVisibleChange}
            onConfirm={() => onToggle(record.id, isSocietyActive ? 0 : 1)}
        >
            <Switch
                disabled={typeof isSocietyActive === 'undefined' || disabled}
                loading={globalStatus === "updating"}
                checkedChildren="Active"
                checked={isSocietyActive}
                unCheckedChildren={typeof isSocietyActive === 'undefined' ? "Loading" : "Inactive"}
            />
        </Popconfirm>
    );
};

const SocietyListingPage = () => {
    useDocumentTitle("Societies");
    const dispatch = useAppDispatch();
    const [columns, setColumns] = useState<ColumnsType<PartialAdminUserType>>();
    const [rows, setRows] = useState<PartialAdminUserType[]>();
    const { societies, cities, status, error, amendmentStatus } = useAppSelector((state) => state.adminServices);
    const [filterState, setFilterState] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [filterCount, setFilterCount] = useState(0);
    const [historyModal, setHistoryModal] = useState<{ id: string; name: string } | null>(null);
    const navigator = useNavigate();
    const getData = useCallback(
        (pageNum = 1, pageSize = 25, filter = "") => {
            dispatch(getSocieties({ pageNo: pageNum, pageSize, filter: filter }));
        },
        [dispatch]
    );

    const toggleStatus = async (societyId: any, newStatus: any) => {
        const result = await dispatch(submitSocietyAmendment({
            entityType: "SOCIETY",
            entityId: String(societyId),
            requestType: "TOGGLE_ACTIVE",
            payload: { toggle: newStatus },
        }));
        if (submitSocietyAmendment.fulfilled.match(result)) {
            message.success("Status change request submitted for approval");
            const filter = searchParams.toString();
            getData(societies.pageNo || 1, societies.pageSize || 25, filter);
        }
    };

    useEffect(() => {
        dispatch(getClusterListing());
        dispatch(getCities());

    }, []);

    useEffect(() => {
        const filter = searchParams.toString();
        setFilterCount(
            Object.keys(Object.fromEntries(new URLSearchParams(filter))).length
        );
        getData(1, 25, filter);
    }, [dispatch, getData, searchParams]);

    useEffect(() => {
        if (status === "loading") {
            message.loading({ content: "Loading", key: MESSAGE_KEY });
        } else if (status === "success") {
            // message.success({
            //     content: "Admin Users Loaded.",
            //     key: MESSAGE_KEY,
            // });
        } else if (status === "error") {
            message.error({ content: error, key: MESSAGE_KEY });
        }

        return () => {
            message.destroy();
        };
    }, [status, error]);

    useEffect(() => {
        const adminUsersData = societies?.data ?? [];
        const data: PartialAdminUserType[] = adminUsersData.map((au, i) => {
            const {
                id, name, latitude, longitude, tower, storeId, pincode, city, state, clusterId, active, cityId, verified, approved, pendingAmendmentId,
            } = au;

            const newRow = {
                id, name, latitude, longitude, tower, storeId, pincode, city, state, clusterId, active, cityId, verified, approved, pendingAmendmentId,
            };
            return { ...newRow, key: `adminusers-${i}`, ellipses: true };
        });

        const tableHeaders: ColumnsType<PartialAdminUserType> = [
            ...Object.keys(headerKeys).map((key) => ({
                key,
                title: headerKeys[key],
                dataIndex: key,
                width: calculateWidth(key),
                render: (text: string, record: PartialAdminUserType) => {

                    if (key === "coordinates") {
                        return <Col>
                            <Row>Latitude: {record.latitude}</Row>
                            <Row>Longitude: {record.longitude}</Row>
                        </Col>;
                    }

                    if (key === "cityId") {
                        if (record.cityId == null) {
                            return <></>;
                        }
                        return <div>{cities.find(e => e.id === record.cityId)?.name}, {cities.find(e => e.id === record.cityId)?.state}</div>;
                    }

                    if (key === "tower") {
                        return <Col>
                            {record.tower?.map(e => (<Row>{e.towerName} || {e.floorCount}</Row>))}
                        </Col>;
                    }

                    if (key === "actions") {
                        const hasPendingAmendment = !!record.pendingAmendmentId;
                        return (
                            <Row
                                align="middle"
                                gutter={8}
                                style={{ display: "flex", flexWrap: "nowrap" }}
                            >
                                {record.active == 1 && (
                                    <Col>
                                        {hasPendingAmendment ? (
                                            <span
                                                style={{
                                                    opacity: 0.4,
                                                    cursor: "not-allowed",
                                                    border: "1px solid",
                                                    padding: "0.2rem",
                                                    borderRadius: "50%",
                                                    width: "1.5rem",
                                                    height: "1.5rem",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <MdEdit />
                                            </span>
                                        ) : (
                                            <StyledActionLink to={`/admin-services/create-society/${record.id}`}>
                                                <MdEdit />
                                            </StyledActionLink>
                                        )}
                                    </Col>
                                )}
                                <Col>
                                    <Tooltip title="View amendment history">
                                        <StyledActionButton
                                            type="button"
                                            onClick={() => setHistoryModal({ id: String(record.id), name: record.name ?? "" })}
                                        >
                                            <MdHistory />
                                        </StyledActionButton>
                                    </Tooltip>
                                </Col>
                            </Row>
                        );
                    }

                    if (key === "verified") {
                        const isVerified = (record as Society).verified === 1;
                        return (
                            <span style={{ fontWeight: 500 }}>
                                {isVerified ? 'Verified' : 'Unverified'}
                            </span>
                        );
                    }

                    if (key === "approved") {
                        const isApproved = (record as any).approved === 1;
                        return (
                            <span style={{ fontWeight: 500 }}>
                                {isApproved ? 'Approved' : 'Approval Pending'}
                            </span>
                        );
                    }

                    if (key === "status") {
                        const hasPendingAmendment = !!record.pendingAmendmentId;
                        return (
                            <>
                                {hasPendingAmendment && (
                                    <Tag color="gold" style={{ marginBottom: 4 }}>
                                        Pending #{record.pendingAmendmentId}
                                    </Tag>
                                )}
                                <SocietyStatusToggle
                                    record={record}
                                    globalStatus={amendmentStatus === "saving" ? "updating" : status}
                                    onToggle={toggleStatus}
                                    disabled={hasPendingAmendment}
                                />
                            </>
                        );
                    }

                    return <>{text}</>;
                },
            })),
        ];

        setColumns(tableHeaders);
        setRows(data);
    }, [societies, cities]);

    const Paginator = () => {
        if (societies && societies.pageNo !== undefined) {
            const { pageNo, pageSize, total } = societies;
            return (
                <StyledPagination
                    current={pageNo}
                    pageSize={pageSize}
                    total={total}
                    onChange={(page, pageSize) => {
                        const filter = searchParams.toString();
                        setFilterCount(
                            Object.keys(Object.fromEntries(new URLSearchParams(filter))).length
                        );
                        getData(page, pageSize, filter);
                    }}
                />
            );
        }

        return <></>;
    };

    return (
        <>
            <Row>
                <Col>
                    <AppPageHeader title="All Societies List" />
                </Col>
            </Row>
            <Row>
                <AppActionStrip>
                    <div className="action-strip__item">
                        <AppFilterButton
                            active={filterState}
                            onClick={() => setFilterState(!filterState)}
                            filterCount={filterCount}
                        />
                    </div>
                    <Button
                        icon={<BsBuilding style={{ marginRight: '.5rem' }} />}
                        type={"primary"}
                        onClick={() => navigator("/admin-services/create-society")}
                    >
                        Add Society Request
                    </Button>
                </AppActionStrip>
            </Row>
            <Row style={{ marginBottom: "1rem" }}>
                <AppFilter expanded={filterState}>
                    <AppSocietyFilter />
                </AppFilter>
            </Row>
            {rows && rows.length ? (
                <Row>
                    <Col xs={24}>
                        <Table
                            columns={columns}
                            dataSource={rows}
                            scroll={{ x: "100vh", y: "65vh" }}
                            size="small"
                            pagination={false}
                            loading={status === "loading"}
                            bordered
                        />
                    </Col>
                    <Col xs={24}>
                        <Paginator />
                    </Col>
                </Row>
            ) : (
                status !== "loading" && <>No Societies to show.</>
            )}
            <SocietyHistoryModal
                open={!!historyModal}
                societyId={historyModal?.id ?? null}
                societyName={historyModal?.name}
                onClose={() => setHistoryModal(null)}
            />
        </>
    );
};

export default SocietyListingPage;
function calculateWidth(key: string): number | undefined {
    if (key == "id") {
        return 50;
    }
    if (key == "storeId") {
        return 120;
    }
    if (key == "clusterId") {
        return 80;
    }
    if (key == "pincode") {
        return 80;
    }
}